import { makeAutoObservable, runInAction } from 'mobx';
import type { Tariff, TariffFormData } from '@/models/tariff/types';
import { emptyFormData } from '@/models/tariff/types';
import {
  fetchTariffs,
  fetchTariff,
  createTariff,
  updateTariff,
  deleteTariff,
} from '@/models/tariff/service';
import { errorModalStore } from '@/stores/errorModal.store';
import { pluralize } from '@/lib/pluralize';
import { toastStore } from '@/stores/toast.store';
import type { CameraArchiveDepth } from '@/models/camera/types';
import { fetchCameraArchiveDepth } from '@/models/camera/service';
import type { Country } from '@/models/country/types';
import { fetchCountries } from '@/models/country/service';
import { currencySign } from '@/lib/currency';
import type { AccountType } from '@/models/account/types';
import { fetchAccountTypes } from '@/models/account/service';

class TariffStore {
  // --- серверный стейт ---
  tariffs: Tariff[] = [];
  managerTariffs: Tariff[] = [];
  isLoading = false;
  isRefreshing = false;
  error: string | null = null;
  archiveDepths: CameraArchiveDepth[] = [];
  countries: Country[] = [];
  accountTypes: AccountType[] = [];

  // --- UI стейт ---
  search = '';
  isModalOpen = false;
  editingTariff: Tariff | null = null;
  formData: TariffFormData = { ...emptyFormData };
  formError: string | null = null;
  isSaving = false;
  deleteTarget: Tariff | null = null;
  isDeleting = false;

  constructor() {
    makeAutoObservable(this);
  }

  // --- computed ---
  get countryCurrencyMap(): Map<string, string> {
    return new Map(this.countries.map((c) => [c.name, currencySign(c)]));
  }

  // Отдаёт ли бэкенд в списке признак показа в приложении — от этого зависит колонка «В приложении»
  get hasAppVisibility() {
    return this.tariffs.some((tariff) => tariff.is_showed != null);
  }

  get filtered() {
    const q = this.search.toLowerCase();
    return this.tariffs.filter(
      (tariff) =>
        (tariff.tariff_name ?? '').toLowerCase().includes(q) ||
        (tariff.description ?? '').toLowerCase().includes(q)
    );
  }

  // --- actions ---
  setSearch = (value: string) => {
    this.search = value;
  };

  setField = <K extends keyof TariffFormData>(key: K, value: TariffFormData[K]) => {
    this.formData[key] = value;
  };

  openCreate = () => {
    this.editingTariff = null;
    this.formData = { ...emptyFormData };
    this.formError = null;
    this.isModalOpen = true;
  };

  openEdit = async (tariff: Tariff) => {
    this.editingTariff = tariff;
    this.formData = { ...emptyFormData };
    this.formError = null;
    this.isSaving = true;
    try {
      const detail = await fetchTariff(tariff.id_tariff);
      runInAction(() => {
        this.formData = {
          tariff_name: detail.tariff_name,
          ab_plata: String(detail.ab_plata),
          description: detail.description,
          id_country: String(detail.id_country),
          id_account_type: String(detail.id_account_type),
          id_archive_period: String(detail.id_archive_period),
          test_days: String(detail.test_days),
          tariff_length: String(detail.tariff_length ?? ''),
          is_showed: detail.is_showed === 1,
          for_oper: detail.for_oper === 1,
          is_start: String(detail.is_start ?? 0),
          id_tariff_vc: String(detail.id_tariff_vc ?? ''),
          users_choice: detail.users_choice === 1,
          periods_count: String(detail.periods_count ?? ''),
          show_if_count_abon_cameras_is: String(detail.show_if_count_abon_cameras_is ?? ''),
          has_discount: detail.has_discount === 1,
          bonus_days: String(detail.bonus_days ?? ''),
          id_tariff_after_bonus: String(detail.id_tariff_after_bonus ?? ''),
          for_move: detail.for_move === 1,
          is_tariff_lv_monitor: detail.is_tariff_lv_monitor === 1,
          start_tariff_lv_monitor: String(detail.start_tariff_lv_monitor ?? ''),
          no_show_tariff_with_location: detail.no_show_tariff_with_location === 1,
          account_description: detail.account_description,
          desc_recomm_pay: detail.desc_recomm_pay,
        };
        this.isModalOpen = true;
        this.isSaving = false;
      });
    } catch (e) {
      runInAction(() => {
        this.formError = (e as Error).message;
        this.isModalOpen = true;
        this.isSaving = false;
      });
    }
  };

  closeModal = () => {
    this.isModalOpen = false;
    this.editingTariff = null;
    this.formData = { ...emptyFormData };
    this.formError = null;
  };

  setDeleteTarget = (tariff: Tariff | null) => {
    if (tariff && (tariff.count_cameras ?? 0) > 0) {
      const name = tariff.tariff_name ?? '—';
      const n = tariff.count_cameras ?? 0;
      const noun = pluralize(n, 'привязанная камера', 'привязанные камеры', 'привязанных камер');
      errorModalStore.show(`Нельзя удалить тариф «${name}»: на нём ${n} ${noun}.`);
      return;
    }
    this.deleteTarget = tariff;
  };

  // --- async actions ---
  fetchArchiveDepths = async () => {
    const depths = await fetchCameraArchiveDepth('1');
    runInAction(() => {
      this.archiveDepths = depths;
    });
  };

  fetchFormDictionaries = async () => {
    const [countries, accountTypes] = await Promise.all([fetchCountries('1'), fetchAccountTypes()]);
    runInAction(() => {
      this.countries = countries;
      this.accountTypes = accountTypes;
    });
  };

  fetchAll = async () => {
    this.isLoading = true;
    this.error = null;
    try {
      const tariffs = await fetchTariffs('0');
      runInAction(() => {
        this.tariffs = tariffs;
        this.isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Ошибка загрузки';
        this.isLoading = false;
      });
    }
  };

  fetchForManager = async () => {
    const tariffs = await fetchTariffs('1');
    runInAction(() => {
      this.managerTariffs = tariffs;
    });
  };

  save = async () => {
    if (!this.formData.tariff_name.trim()) {
      this.formError = 'Введите название тарифа';
      return false;
    }
    if (!this.formData.id_country) {
      this.formError = 'Выберите страну';
      return false;
    }
    if (!this.formData.id_account_type) {
      this.formError = 'Выберите тип аккаунта';
      return false;
    }
    this.isSaving = true;
    this.formError = null;
    try {
      if (this.editingTariff) {
        await updateTariff(this.editingTariff.id_tariff, this.formData);
      } else {
        await createTariff(this.formData);
      }
      const isEdit = !!this.editingTariff;
      runInAction(() => {
        this.closeModal();
        this.isSaving = false;
      });
      toastStore.show(isEdit ? 'Тариф обновлён' : 'Тариф создан');
      this.refreshSilently();
      return true;
    } catch (err) {
      runInAction(() => {
        this.formError = err instanceof Error ? err.message : 'Ошибка сохранения';
        this.isSaving = false;
      });
      return false;
    }
  };

  refreshSilently = async () => {
    this.isRefreshing = true;
    try {
      const tariffs = await fetchTariffs('0');
      runInAction(() => {
        this.tariffs = tariffs;
        this.isRefreshing = false;
      });
    } catch {
      runInAction(() => {
        this.isRefreshing = false;
      });
    }
  };

  confirmDelete = async () => {
    if (!this.deleteTarget) return;
    this.isDeleting = true;
    try {
      await deleteTariff(this.deleteTarget.id_tariff);
      runInAction(() => {
        this.tariffs = this.tariffs.filter(
          (tariff) => tariff.id_tariff !== this.deleteTarget!.id_tariff
        );
        this.deleteTarget = null;
        this.isDeleting = false;
      });
    } catch (e) {
      runInAction(() => {
        this.deleteTarget = null;
        this.isDeleting = false;
      });
      errorModalStore.show(e instanceof Error ? e.message : 'Ошибка удаления тарифа');
    }
  };
}

export const tariffStore = new TariffStore();
